/**
 * AgentOverviewDashboard — the Dashboard rendered for a sales-agent
 * login. Where the default Dashboard shows company-wide contact
 * analytics, this is scoped entirely to the signed-in agent: their
 * leads, deals, reports, visits, tasks, meetings, attendance and
 * field-work time — so an agent can track their own performance.
 *
 * Data: GET /api/dashboard/agent-overview (everything filtered to the
 * agent's own id server-side).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import HelpInfo from '@/components/HelpInfo';
import DirhamSign from '@/components/DirhamSign';
import SLAPerformanceCard from '@/components/SLAPerformanceCard';
import {
  Users, Target, FileText, MapPin, CheckSquare, CalendarClock,
  Briefcase, Clock, Timer, RefreshCw, TrendingUp, ChevronRight, Activity,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtMins = (m) => {
  if (!m || m < 0) return '0m';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h ? `${h}h ${mm}m` : `${mm}m`;
};

// Lifecycle-stage colours — matched to the "Change Lifecycle Stage"
// picker so a status reads the same colour everywhere in the app.
const STAGE_COLORS = {
  prequalified: { label: 'Pre-qualified', color: '#7DD3FC' },     // light blue
  cold:         { label: 'Cold', color: '#FACC15' },              // yellow
  warm:         { label: 'Warm', color: '#F59E0B' },              // amber
  hot:          { label: 'Hot', color: '#F97316' },               // orange
  closed:       { label: 'Closed', color: '#22C55E' },            // green
  lost:         { label: 'Lost', color: '#EF4444' },              // red
  broker:       { label: 'Broker Contacted', color: '#A78BFA' },  // purple
};

// Classify a lead status into a lifecycle stage so its bar takes the
// matching colour. Order matters — "Lost" / "Not Interested" is caught
// before the "interested" → Hot rule.
const stageOf = (status) => {
  const s = (status || '').toLowerCase();
  if (/lost|not interested|dead|unqualified/.test(s)) return STAGE_COLORS.lost;
  if (/closed|\bwon\b|convert|booking/.test(s)) return STAGE_COLORS.closed;
  if (/broker/.test(s)) return STAGE_COLORS.broker;
  if (/\bhot\b|negotiat|proposal|meeting|site.?visit|sales qualified|^qualified/.test(s)) return STAGE_COLORS.hot;
  if (/\bcold\b|no.?response/.test(s)) return STAGE_COLORS.cold;
  if (/pre.?qualif|^new|new lead|pending|under qualif/.test(s)) return STAGE_COLORS.prequalified;
  return STAGE_COLORS.warm; // active / contacted / in-progress
};

// Maps a raw status string to a friendly label + dot colour for the
// attendance card (mirrors the Agent Status panel's scheme).
const statusView = (s) => {
  const v = (s || '').toLowerCase();
  if (['in_office', 'inside'].includes(v)) return { label: 'In Office', dot: 'bg-emerald-500' };
  if (['on_leave', 'leave'].includes(v)) return { label: 'On Leave', dot: 'bg-blue-500' };
  if (['no_activity', 'idle'].includes(v)) return { label: 'No Activity Yet', dot: 'bg-slate-400' };
  return { label: 'In Field', dot: 'bg-rose-500' };
};

const AgentOverviewDashboard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/dashboard/agent-overview?token=${encodeURIComponent(token)}`);
      const json = await res.json();
      if (json.success) setData(json);
      else toast.error(json.detail || 'Could not load your dashboard');
    } catch (e) {
      toast.error(`Load failed: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const k = data?.kpis || {};
  const att = data?.attendance || {};
  const leadStatus = data?.lead_status || [];
  const agentName = data?.agent?.name || user?.full_name || 'Agent';
  const sv = statusView(att.current_status);

  // KPI tiles — the activities an agent tracks day to day. Each tile
  // is clickable and routes to where that detail lives.
  const kpis = [
    { label: 'Assigned Leads', value: k.assigned_leads ?? 0, icon: Users, tone: 'violet',
      sub: 'Leads you own', route: '/contacts' },
    { label: 'Closed Deals', value: k.closed_deals ?? 0, icon: Target, tone: 'emerald',
      sub: <><DirhamSign /> {(k.deals_value || 0).toLocaleString()} won</>, route: '/my-dashboard' },
    { label: 'Open Deals', value: k.open_deals ?? 0, icon: Briefcase, tone: 'blue',
      sub: 'Still in pipeline', route: '/my-dashboard' },
    { label: 'Daily Reports', value: k.daily_reports ?? 0, icon: FileText, tone: 'violet',
      sub: `${k.daily_reports_month ?? 0} this month`, route: '/my-dashboard' },
    { label: 'Site Visit Reports', value: k.site_visits ?? 0, icon: MapPin, tone: 'emerald',
      sub: `${k.site_visits_submitted ?? 0} submitted`, route: '/my-dashboard' },
    { label: 'Pending Tasks', value: k.pending_tasks ?? 0, icon: CheckSquare, tone: 'amber',
      sub: `${k.completed_tasks ?? 0} completed`, route: '/my-dashboard' },
    { label: 'Follow-ups Today', value: k.follow_ups_today ?? 0, icon: CalendarClock, tone: 'rose',
      sub: 'Due today', route: '/my-dashboard' },
    { label: 'Client Meetings', value: k.client_meetings ?? 0, icon: CalendarClock, tone: 'blue',
      sub: 'Logged meetings', route: '/meeting-intelligence/meetings' },
    { label: 'Conversion Rate', value: `${k.conversion_rate ?? 0}%`, icon: TrendingUp, tone: 'emerald',
      sub: 'Leads won to date', route: '/my-dashboard' },
    { label: 'Activities Today', value: k.activities_today ?? 0, icon: Activity, tone: 'amber',
      sub: 'Logged today', route: '/my-dashboard' },
  ];

  const toneText = {
    violet: 'text-violet-600', emerald: 'text-emerald-600', amber: 'text-amber-600',
    blue: 'text-blue-600', rose: 'text-rose-600',
  };
  const toneBg = {
    violet: 'bg-violet-100', emerald: 'bg-emerald-100', amber: 'bg-amber-100',
    blue: 'bg-blue-100', rose: 'bg-rose-100',
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6 text-violet-600" />
            My Dashboard Overview
            <HelpInfo
              align="inline"
              title="How to use My Dashboard Overview"
              steps={[
                'Everything here is yours — your leads, deals, reports, visits, tasks and field-work time. Nothing is company-wide.',
                'The tiles up top are your activity scoreboard: assigned leads, closed deals, daily reports, site visits, pending tasks and follow-ups.',
                'The Attendance card shows your current status and how long you\'ve been working today; Field Work shows time logged on site visits.',
                <>Use <strong>Open My Pipeline</strong> to work your deals and follow-ups in detail.</>,
              ]}
              tip="Check Follow-ups Today first thing each morning — clearing those is the fastest way to keep deals moving."
            />
          </h1>
          <p className="text-muted-foreground mt-1">
            Welcome back, {agentName} — here's your performance at a glance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/my-dashboard')}>
            Open My Pipeline <ChevronRight size={14} className="ml-1" />
          </Button>
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={fetchData} title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Attendance + Field Work strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Attendance</p>
                <p className="text-lg font-semibold mt-1 flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${sv.dot}`} />
                  {sv.label}
                </p>
              </div>
              <Clock className="h-7 w-7 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Working Hours Today</p>
                <p className="text-lg font-semibold mt-1">{fmtMins(att.working_minutes)}</p>
                <p className="text-[11px] text-slate-400">
                  {att.checked_in_at
                    ? `Since ${new Date(att.checked_in_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                    : 'No activity logged yet today'}
                </p>
              </div>
              <Timer className="h-7 w-7 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Travel &amp; Field Work</p>
                <p className="text-lg font-semibold mt-1">{fmtMins(k.field_work_minutes)}</p>
                <p className="text-[11px] text-slate-400">
                  {fmtMins(k.field_work_minutes_today)} on site today
                </p>
              </div>
              <MapPin className="h-7 w-7 text-slate-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((c) => {
          const Icon = c.icon;
          return (
            <Card
              key={c.label}
              role="button"
              tabIndex={0}
              onClick={() => navigate(c.route)}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(c.route); }}
              className="border-slate-200 cursor-pointer transition-all hover:shadow-md hover:border-violet-300"
              title={`View ${c.label}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500">{c.label}</p>
                    <p className={`text-2xl font-bold mt-1 ${toneText[c.tone]}`}>
                      {loading ? '—' : c.value}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5 truncate">{c.sub}</p>
                  </div>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${toneBg[c.tone]}`}>
                    <Icon size={16} className={toneText[c.tone]} />
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-2 text-[11px] text-violet-600">
                  View details <ChevronRight size={11} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* SLA performance — daily KPI strip + recently missed leads.
          Renders for every agent; the panel itself handles the empty
          state when there's no SLA-tracked activity in the window. */}
      <SLAPerformanceCard />

      {/* Lead Status breakdown */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            <TrendingUp size={16} className="text-violet-600" />
            My Lead Status
            <HelpInfo
              align="inline"
              title="How to read My Lead Status"
              steps={[
                'A breakdown of the leads assigned to you, grouped by their current status and sorted biggest-first.',
                'Each bar\'s length is that status\'s size relative to your largest status — the longest bar is your most common status.',
                <>Bars are coloured by lifecycle stage — Pre-qualified, Cold, Warm, Hot, Closed, Lost and Broker Contacted — the same colours as the <em>Change Lifecycle Stage</em> picker. The chip on each row names the stage.</>,
                'Move leads forward by logging calls and follow-ups — the status updates as you work them.',
              ]}
              tip="Hot-coloured rows are closest to a deal — work those first, then warm, then the fresh ones."
            />
          </CardTitle>
          <CardDescription>Leads assigned to you, by status — coloured by lifecycle stage</CardDescription>
        </CardHeader>
        <CardContent>
          {leadStatus.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">No leads assigned to you yet.</p>
          ) : (
            <>
              {/* Legend — only the stages that actually appear below. */}
              <div className="flex items-center gap-x-4 gap-y-1 mb-3 text-xs text-slate-500 flex-wrap">
                {(() => {
                  const seen = [];
                  leadStatus.forEach((s) => {
                    const st = stageOf(s.status);
                    if (!seen.some((x) => x.label === st.label)) seen.push(st);
                  });
                  return seen.map((st) => (
                    <span key={st.label} className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: st.color }} />
                      {st.label}
                    </span>
                  ));
                })()}
              </div>
              <p className="text-[11px] text-slate-400 mb-2">Click a status to open and work those leads →</p>
              <div className="space-y-1">
                {(() => {
                  const max = Math.max(...leadStatus.map((s) => s.count), 1);
                  const agentId = data?.agent?.id || user?.id;
                  const openStatus = (status) => {
                    const params = new URLSearchParams({ status });
                    if (agentId) params.set('owner_id', agentId);
                    navigate(`/contacts?${params.toString()}`);
                  };
                  return leadStatus.map((s) => {
                    const st = stageOf(s.status);
                    return (
                      <div
                        key={s.status}
                        role="button"
                        tabIndex={0}
                        onClick={() => openStatus(s.status)}
                        onKeyDown={(e) => { if (e.key === 'Enter') openStatus(s.status); }}
                        className="flex items-center gap-3 rounded-md px-2 py-1.5 -mx-2 cursor-pointer hover:bg-slate-50 transition-colors"
                        title={`Open your ${s.status} leads`}
                      >
                        <span className="text-sm text-slate-700 capitalize w-40 truncate">{s.status}</span>
                        <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${(s.count / max) * 100}%`, backgroundColor: st.color }}
                          />
                        </div>
                        <span
                          className="text-[10px] font-medium px-1.5 py-0.5 rounded w-24 text-center"
                          style={{ backgroundColor: `${st.color}22`, color: st.color }}
                        >
                          {st.label}
                        </span>
                        <Badge variant="outline" className="text-xs w-10 justify-center">{s.count}</Badge>
                        <ChevronRight size={14} className="text-slate-300 flex-shrink-0" />
                      </div>
                    );
                  });
                })()}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentOverviewDashboard;
